import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  isKoreanHoliday,
  getHolidayName,
  isWeekend,
  ServiceOrderCategory,
  type CalendarEventDto,
  type CalendarRenewalDto,
  type CalendarReplacementDto,
} from '@bhops/shared';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { listAllServiceOrders } from '@/lib/list-all-service-orders';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { activeCustomersApi } from '@/api/active-customers';
import { NewScheduleModal } from './NewScheduleModal';
import { CompleteReplacementModal } from './CompleteReplacementModal';
import { CompleteRenewalModal, UndoRenewalDialog } from './CompleteRenewalModal';
import { renewalSubtitle, renewalTitle } from './renewal-label';
import { EventEditModal, EventStageDialog } from './EventEditModal';
import { eventChipTitle, eventSubtitle } from './event-label';
import {
  DeleteScheduleDialog,
  EditScheduleModal,
  ScheduleContextMenu,
  maintenanceMemoOf,
  type ScheduleMove,
} from './ScheduleActions';
import { extractApiError } from '@/api/client';
import { useOptionalToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { kstMonthCursor, kstTodayYmd, kstYmdHyphen } from '@/lib/format-date';
import { Icon } from '@/components/Icon';
import {
  type CalendarCategory,
  CATEGORY_META,
  CATEGORY_ORDER,
  DEFAULT_ACTIVE,
  loadActiveCategories,
  saveActiveCategories,
} from './calendar-categories';
import { replacementSubtitle, replacementTitle } from './replacement-label';
import { maintenanceHref } from '@/lib/maintenance-link';

// 규칙1(이모지 금지) — 카테고리→Icon 픽토그램은 공유 CATEGORY_META.iconName 을
// 그대로 읽는다. 종전에는 여기에 같은 표를 한 벌 더 두고 있어 한쪽만 고치면
// 범례와 셀의 그림이 갈라졌다.

// Plan §B-1 (PDF p.3) — 한 눈에 일정을 파악할 수 있게 캘린더가
// 필요함 (휴일/근로자의 날 등 네이버 달력 활용).
//
// 사용자 메모 (plan.md §4-2): 카테고리 3 종으로 축소.
//   🚚 로봇 설치 예정      ← InfrastructureOrder.installScheduledAt
//   📅 그릴러 납품 예정    ← InfrastructureOrder.grillDeliveredAt
//   🛠️ AS / 점검 예정     ← Report.scheduledAt (Phase 5+ 추가 예정)
//
// "계약 만료" 카테고리는 사용자 결정으로 제외 — KPI / 알림으로만.
//
// S3 Phase 3.7 (spec §3.8) — 설치 중심 + 카테고리 토글:
//   🚚 로봇 설치   ← InfrastructureOrder.installScheduledAt (기본 ON)
//   🛠️ 유지보수    ← ServiceOrder.scheduledFor (PREVENTIVE/REACTIVE, 기본 OFF)
//   📦 그릴러 납품  ← InfrastructureOrder.grillDeliveredAt (기본 OFF)
// 표시 토글 바(체크박스)로 카테고리별 표시/숨김, 선택 상태는 localStorage 저장.
// (기존 'AS · 점검'(service) 카테고리는 데이터 소스가 없어 빈 카테고리였고,
//  본 Phase 에서 ServiceOrder 를 데이터 소스로 가진 '유지보수'(maintenance)로 대체.)

// FB §6 (결정 D) — '발주'(order, grillOrderedAt) 카테고리 추가. 인프라 발주
// 페이지에서 발주·납품·설치를 한 캘린더에서 보기 위함. 대시보드 기본은
// 종전대로 '설치'만 ON (발주/납품/유지보수 OFF) — DEFAULT_ACTIVE 참조.
// FB2 §2 (결정 H) — 카테고리 type·META·ORDER·DEFAULT_ACTIVE·load/save 헬퍼는
// UpcomingEventsPanel 과 공유하기 위해 ./calendar-categories 로 추출됨.
interface CalendarEvent {
  id: string;
  date: string; // YYYY-MM-DD
  category: CalendarCategory;
  title: string;
  href?: string;
  subtitle?: string;
  /** 2026-10-19 — 시각 'HH:mm'. 없으면 하루 종일(칩 맨 위에 선다). */
  time?: string | null;
  /** 2026-10-20 — 캘린더에서 잡은 유지보수의 메모 · 그런 유지보수인가(우클릭 수정 · 삭제). */
  memo?: string | null;
  fromCalendar?: boolean;
  /** 2026-11-02 — 캘린더에서 잡은 유지보수가 올린 고객사 리포트의 번호(삭제 안내에 쓴다). */
  reportNo?: string | null;
  /**
   * 끌어 옮길 수 있는 일정이면 무엇을 옮기는지(2026-09-30 오너 요청). 설치 **완료**는 이미
   * 일어난 사실이라 옮기지 않는다.
   */
  move?:
    | { kind: 'infra'; id: string; field: 'order' | 'delivery' | 'install' }
    | { kind: 'maintenance'; id: string }
    | { kind: 'return'; id: string }
    | { kind: 'replace'; id: string }
    | { kind: 'renewal'; id: string }
    | { kind: 'event'; id: string; side: 'install' | 'return' };
  /** 2026-11-02 — 이벤트 일정이면 그 한 줄(누르면 이벤트 창, 회수 칩 우클릭 [회수 완료]). */
  calendarEvent?: CalendarEventDto;
  /** 2026-10-20 — 로봇 교체 일정이면 그 한 줄(우클릭 [교체 완료]가 쓴다). */
  replacement?: CalendarReplacementDto;
  /** 2026-10-22 — 재계약 일정이면 그 한 줄(눌러서 [재계약 완료], 우클릭 [되돌리기]). */
  renewal?: CalendarRenewalDto;
}

/** 끌어 옮기는 일정의 전달 형식(dataTransfer). */
const DRAG_MIME = 'application/x-bhops-calendar-event';

// S3 Phase 3.7 (spec §3.8) — 캘린더 전용 localStorage 저장키. (기본값/헬퍼는
// 공유 모듈에서, 저장키만 소비처별로 다르게 둔다 — 패널은 별도 키 사용.)
const CATEGORY_STORAGE_KEY = 'kuun:integrated-calendar:categories';

interface Props {
  /** Initial month to display (YYYY-MM-DD; day component is ignored). Defaults to today. */
  initialMonth?: string;
  /**
   * Plan v0.3.0 §C-1 (PPTX Slide 3) — when the dashboard places the
   * calendar in a 3:2 split next to UpcomingEventsPanel, the calendar
   * needs to fit a narrower column. `compact: true` shrinks day cells
   * (80→58 min) and trims the per-cell event cap (3→2). Header / nav /
   * weekday strip remain identical so the shape is still legible at a
   * glance. (S3 Phase 3.7 — 카테고리 토글 바는 컨트롤이므로 compact
   * 에서도 표시한다.)
   */
  compact?: boolean;
  /**
   * FB §6 — 페이지별 토글 기본값 override. 미지정 키는 전역 DEFAULT_ACTIVE 를
   * 따른다. 예: 인프라 발주 페이지는 `{ install:true, order:true, delivery:true }`
   * 로 발주·납품·설치를 기본 ON. (대시보드는 미지정 → 설치만 ON 유지.)
   */
  defaultActive?: Partial<Record<CalendarCategory, boolean>>;
  /**
   * FB §6 — localStorage 저장키 override. 페이지마다 다른 키를 주면 토글
   * 선호가 페이지별로 독립 저장되어, 한 페이지의 기본값이 다른 페이지의
   * 저장값을 덮어쓰지 않는다. 미지정 시 전역 공유 키(대시보드 동작 불변).
   */
  storageKey?: string;
}

export function IntegratedCalendar({
  initialMonth,
  compact = false,
  defaultActive,
  storageKey = CATEGORY_STORAGE_KEY,
}: Props) {
  // 한국시간 통일 5차(26-08-14) — 격자 커서를 KST 로. 종전 `new Date().getUTCMonth()`
  // 는 한국 1월 1일 오전 0~9시에 **작년 12월**을 그렸다(그때 UTC 는 아직 12/31).
  const [cursor, setCursor] = useState(() => kstMonthCursor(initialMonth));

  // 해소된 기본값 = 전역 DEFAULT_ACTIVE 에 페이지 override 를 병합.
  const resolvedDefaults = useMemo<Record<CalendarCategory, boolean>>(
    () => ({ ...DEFAULT_ACTIVE, ...defaultActive }),
    [defaultActive],
  );

  // S3 Phase 3.7 (spec §3.8) — 카테고리 표시 토글 상태 (localStorage 영속).
  // FB §6 — storageKey/기본값을 주입받아 페이지별 독립 영속.
  const [activeCategories, setActiveCategories] = useState<Record<CalendarCategory, boolean>>(() =>
    loadActiveCategories(storageKey, resolvedDefaults),
  );

  useEffect(() => {
    saveActiveCategories(storageKey, activeCategories);
  }, [storageKey, activeCategories]);

  function toggleCategory(cat: CalendarCategory): void {
    setActiveCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  // §E-3 — month-window query for InfrastructureOrders. The date range
  // is the calendar grid (Sun before month start → Sat after month end)
  // so dates from neighboring months that fall on the visible grid are
  // also fetched.
  const range = useMemo(() => monthGridRange(cursor.year, cursor.month), [cursor]);

  const ordersQ = useQuery({
    queryKey: ['dashboard', 'calendar', range.start, range.end],
    queryFn: () =>
      infrastructureOrdersApi.list({
        installFrom: range.start,
        installTo: range.end,
      }),
    staleTime: 60_000,
  });

  // S3 Phase 3.7 (spec §3.8) — 유지보수(ServiceOrder) 일정. 예방정비
  // (PREVENTIVE) / 고장대응(REACTIVE) 의 scheduledFor 가 데이터 소스.
  // 유지보수 토글이 켜져 있을 때만 fetch (기본 OFF 라 평상시 호출 안 함).
  const maintenanceQ = useQuery({
    queryKey: ['dashboard', 'calendar', 'maintenance', range.start, range.end],
    enabled: activeCategories.maintenance,
    queryFn: () =>
      // 서버는 한 번에 100건까지 — 200 으로 부르면 400 이라 유지보수가 하나도 안 보였다(2026-10-19).
      listAllServiceOrders({ category: ServiceOrderCategory.PREVENTIVE }),
    staleTime: 60_000,
  });

  // 2026-10-19 — 회수(Customer.returnScheduledAt). 콘솔 '활성 고객사'와 같은 목록에서 회수일이 있는
  // 고객사. 회수 토글이 켜져 있을 때만 부른다.
  const returnsQ = useQuery({
    queryKey: ['dashboard', 'calendar', 'returns'],
    enabled: activeCategories.return,
    queryFn: () => activeCustomersApi.list(),
    staleTime: 60_000,
  });

  // 2026-10-20 — 로봇 교체('로봇 교체' 서비스 오더). 토글이 켜져 있을 때만 부른다.
  const replacementsQ = useQuery({
    queryKey: ['dashboard', 'calendar', 'replacements'],
    enabled: activeCategories.replace,
    queryFn: () => scheduleDatesApi.listReplacements(),
    staleTime: 60_000,
  });

  // 2026-10-22 — 재계약(옛 고객사 → 새 고객사). 토글이 켜져 있을 때만 부른다.
  const renewalsQ = useQuery({
    queryKey: ['dashboard', 'calendar', 'renewals'],
    enabled: activeCategories.renewal,
    queryFn: () => scheduleDatesApi.listRenewals(),
    staleTime: 60_000,
  });

  // 2026-11-02 — 이벤트(행사장 설치 · 회수). 토글이 켜져 있을 때만 부른다.
  const eventsQ = useQuery({
    queryKey: ['dashboard', 'calendar', 'events'],
    enabled: activeCategories.event,
    queryFn: () => scheduleDatesApi.listEvents(),
    staleTime: 60_000,
  });

  // 유지보수 OFF→ON 시점에 REACTIVE 도 함께 보이도록 두 카테고리를 병합
  // fetch 하는 대신, 가장 흔한 예방정비(PREVENTIVE)만 우선 표시한다. (REACTIVE
  // 는 신고 대응으로 scheduledFor 가 없는 경우가 많아 캘린더 노이즈가 큼.)

  // Reduce orders → events (one row may produce 0, 1, or 2 events
  // depending on which fields are populated).
  //
  // 한국시간 통일 5차(26-08-14) — 이벤트 키를 **KST** 로 뽑는다(종전 toYmd = UTC 자르기).
  // 왜 KST 가 정답인가 — 이 화면이 읽는 값의 저장 관례가 세 가지 섞여 있다:
  //   ⓐ 지금의 인프라 발주 폼: `YYYY-MM-DD` 를 그대로 보내 서버가 **UTC 자정**으로 저장
  //   ⓑ 옛 `ymdToIso` 로 저장된 행: 한국 자정 = **전날 15:00Z**
  //   ⓒ ServiceOrder.scheduledFor: 계약 서명일 + 90일로 계산된 **진짜 순간**
  //      (service-order-auto-create.consumer.ts) — 벽시계 시각이 붙어 있다
  // KST 로 읽으면 ⓐ 는 그대로(자정+9h = 같은 날 09:00), ⓑ·ⓒ 는 운영자가 아는
  // 날짜로 바로 선다. UTC 로 자르면 ⓑ·ⓒ 가 **하루 이른 칸**에 붙는다.
  // 같은 발주의 설치예정일을 인프라 목록(InfrastructurePage 는 kstYmd)과 이 캘린더가
  // 다른 날로 보여 주던 원인이 이것이다.
  const allEvents = useMemo(() => {
    const out: CalendarEvent[] = [];
    for (const o of ordersQ.data ?? []) {
      if (o.installScheduledAt && !o.installCompletedAt) {
        out.push({
          id: `${o.id}-install`,
          date: kstYmdHyphen(o.installScheduledAt) ?? '',
          category: 'install',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.installScheduledTime,
          // FB §3 — 계약 상세 미사용 → 설치 일정 클릭은 고객사 상세로.
          // (InfraOrder DTO 에 customerId 항상 포함.)
          href: `/customers/${o.customerId}`,
          move: { kind: 'infra', id: o.id, field: 'install' },
        });
      }
      // round6 §1-4(b) — 설치 완료 이벤트: 완료 처리해도 캘린더에서 사라지지
      // 않고 완료일에 이력으로 남는다(완료 순간 소멸 → "미반영" 체감의 해소).
      if (o.installCompletedAt) {
        out.push({
          id: `${o.id}-install-done`,
          date: kstYmdHyphen(o.installCompletedAt) ?? '',
          category: 'installDone',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          href: `/customers/${o.customerId}`,
        });
      }
      if (o.grillDeliveredAt) {
        out.push({
          id: `${o.id}-delivery`,
          date: kstYmdHyphen(o.grillDeliveredAt) ?? '',
          category: 'delivery',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.grillDeliveredTime,
          href: `/infrastructure`,
          move: { kind: 'infra', id: o.id, field: 'delivery' },
        });
      }
      // FB §6 (결정 D) — 그릴러 발주(grillOrderedAt) 이벤트. 납품 블록을
      // 미러. 인프라 발주 페이지에서 발주→납품 파이프라인을 캘린더로 추적.
      // (대시보드에서는 'order' 토글 기본 OFF 라 평상시 노출되지 않음.)
      if (o.grillOrderedAt) {
        out.push({
          id: `${o.id}-order`,
          date: kstYmdHyphen(o.grillOrderedAt) ?? '',
          category: 'order',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.grillOrderedTime,
          href: `/infrastructure`,
          move: { kind: 'infra', id: o.id, field: 'order' },
        });
      }
    }
    // S3 Phase 3.7 (spec §3.8) — 유지보수(ServiceOrder) 일정. scheduledFor
    // 가 있는 예방정비 건만 캘린더에 표시.
    // 2026-10-22 — 여러 대 유지보수는 오더 여러 개가 한 일정으로 묶인다(groupId = 첫 오더 id).
    const groupSize = new Map<string, number>();
    for (const so of maintenanceQ.data?.items ?? [])
      if (so.groupId) groupSize.set(so.groupId, (groupSize.get(so.groupId) ?? 0) + 1);
    for (const so of maintenanceQ.data?.items ?? []) {
      if (so.groupId && so.groupId !== so.id) continue;
      const many = so.groupId ? (groupSize.get(so.groupId) ?? 1) : 1;
      if (!so.scheduledFor) continue;
      out.push({
        id: `${so.id}-maintenance`,
        date: kstYmdHyphen(so.scheduledFor) ?? '',
        category: 'maintenance',
        // 2026-10-19 오너 — 칩에는 오더 번호(SO-…) 대신 고객사 이름. 번호는 마우스를 올리면 보인다.
        title: `${
          so.customerDisplayName ??
          so.description?.replace(/^유지보수 — /, '').split('\n')[0] ??
          so.humanId
        }${many > 1 ? ` (${many}대)` : ''}`,
        subtitle: many > 1 ? `${so.humanId} 외 ${many - 1}건` : so.humanId,
        time: so.scheduledTime,
        memo: maintenanceMemoOf(so.description),
        fromCalendar: !!so.faultRemediationId || !!so.sheetReportNo,
        reportNo: so.sheetReportNo ?? null,
        // 2026-10-19 오너 — 누르면 그 일정이 올린 기록으로. 2026-11-02 부터는 고객사 리포트(그 번호).
        href: maintenanceHref(so),
        move: { kind: 'maintenance', id: so.id },
      });
    }
    for (const c of returnsQ.data?.items ?? []) {
      if (!c.returnScheduledAt) continue;
      const robots = c.robotsByModel.reduce((n, g) => n + g.robots.length, 0);
      out.push({
        id: `${c.customerId}-return`,
        date: kstYmdHyphen(c.returnScheduledAt) ?? '',
        category: 'return',
        title: c.displayName,
        subtitle: robots > 0 ? `로봇 ${robots}대` : undefined,
        time: c.returnScheduledTime,
        href: '/field/active-customers',
        move: { kind: 'return', id: c.customerId },
      });
    }
    for (const r of replacementsQ.data?.items ?? []) {
      out.push({
        id: `${r.id}-replace`,
        date: kstYmdHyphen(r.scheduledFor) ?? '',
        category: 'replace',
        title: replacementTitle(r),
        // 회수 → 설치 로봇. 넣을 로봇이 아직 없으면 '배정 전'.
        subtitle: replacementSubtitle(r),
        time: r.scheduledTime,
        memo: r.memo,
        // 2026-10-20 오너 — 누르면 고객사 화면으로 가지 말고 [교체 완료] 창을 연다(href 없음).
        // 마친 교체도 우클릭 수정(실제 교체일까지) · 삭제(교체 되돌리기)와 끌어 옮기기가 된다.
        move: { kind: 'replace' as const, id: r.id },
        replacement: r,
      });
    }
    for (const r of renewalsQ.data?.items ?? []) {
      out.push({
        id: `${r.id}-renewal`,
        date: kstYmdHyphen(r.scheduledFor) ?? '',
        category: 'renewal',
        title: renewalTitle(r),
        subtitle: renewalSubtitle(r),
        time: r.scheduledTime,
        memo: r.memo,
        // 누르면 [재계약 완료] 창(마친 재계약은 우클릭 [되돌리기]).
        move: { kind: 'renewal' as const, id: r.id },
        renewal: r,
      });
    }
    // 2026-11-02 오너 — 설치일은 [이벤트 설치], 회수일은 [이벤트 회수]. 누르면 이벤트 창(href 없음).
    for (const e of eventsQ.data?.items ?? []) {
      for (const side of ['install', 'return'] as const) {
        out.push({
          id: `${e.id}-event-${side}`,
          date: kstYmdHyphen(side === 'install' ? e.installDate : e.returnDate) ?? '',
          category: 'event',
          title: eventChipTitle(e, side),
          subtitle: eventSubtitle(e),
          time: side === 'install' ? e.installTime : e.returnTime,
          move: { kind: 'event' as const, id: e.id, side },
          calendarEvent: e,
        });
      }
    }
    return out;
  }, [
    ordersQ.data,
    maintenanceQ.data,
    returnsQ.data,
    replacementsQ.data,
    renewalsQ.data,
    eventsQ.data,
  ]);

  // S3 Phase 3.7 (spec §3.8) — 활성 카테고리만 캘린더에 표시.
  //
  // 캘린더 일정 누락 수리(2026-08-31) — **격자 범위 가드**를 함께 건다.
  // 왜 한 쌍인가: 이번 수리로 서버가 네 날짜 전부로 행을 찾아 주게 됐다. 그러면
  // "설치일은 이 격자 안인데 납품일은 다음 달"인 행이 더 많이 실려 오고, 그
  // 납품 일정은 격자 밖이라 **칸에는 안 그려지는데 아래 배지에는 세어진다**.
  // 종전에도 그 어긋남이 있었고(8월 보기 "12건"인데 화면엔 7건) 수리 없이
  // 서버만 넓히면 더 벌어진다. 옆 패널(UpcomingEventsPanel)이 이미 쓰는 방식과
  // **같은 기준**(칸 키와 동일한 KST 문자열 비교)으로 자른다.
  //
  // 곁들여 함께 막히는 것 둘:
  //   · 유지보수는 날짜 조건 없이 최대 200건이 매달 똑같이 내려온다 — 격자 밖
  //     예방정비가 배지를 부풀리던 통로가 닫힌다.
  //   · 날짜 파싱 실패로 키가 `''` 인 이벤트는 **어느 달에서도 안 그려지는데**
  //     배지에는 세어지던 유령이었다 — `'' >= range.start` 가 거짓이라 함께 빠진다.
  // ── 끌어 옮기기(2026-09-30 오너 요청 "통합 캘린더에 등록된 일정들도 드래그&드랍으로") ──
  // 놓는 즉시 새 날짜에 보이고(저장 중), 서버가 규칙으로 막으면 제자리로 돌아가며 까닭을 띄운다.
  // 발주를 쓸 수 있는 역할만 끈다(서버도 같은 역할 집합으로 막는다).
  const role = useAuthStore((st) => st.user?.role);
  const canMove = !!role && canEditAny(role);
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [pendingMoves, setPendingMoves] = useState<Record<string, string>>({});
  const [dropDate, setDropDate] = useState<string | null>(null);
  // 2026-10-19 오너 — 날짜(또는 칸의 빈 곳)를 누르면 그날로 일정 추가 창을 연다.
  const [newScheduleDate, setNewScheduleDate] = useState<string | null>(null);
  // 2026-10-20 오너 — 일정 우클릭 → [수정] [삭제]. 삭제는 확인창을 거친다.
  const [menu, setMenu] = useState<{ ev: CalendarEvent; x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<(CalendarEvent & { move: ScheduleMove }) | null>(null);
  const [deleting, setDeleting] = useState<(CalendarEvent & { move: ScheduleMove }) | null>(null);
  // 2026-10-20 오너 — 로봇 교체 일정 우클릭 → [교체 완료].
  const [completing, setCompleting] = useState<CalendarEvent | null>(null);
  // 2026-10-22 — 마친 재계약 우클릭 → [되돌리기].
  const [undoingRenewal, setUndoingRenewal] = useState<CalendarRenewalDto | null>(null);
  // 2026-11-02 — 이벤트 칩을 누르면 이벤트 창. 설치 칩 우클릭 → [설치 완료] · [되돌리기](출고 기록),
  // 회수 칩 우클릭 → [회수 완료] · [되돌리기](반납 · 정비).
  const [openEvent, setOpenEvent] = useState<CalendarEventDto | null>(null);
  const [eventStage, setEventStage] = useState<{
    event: CalendarEventDto;
    stage: 'install' | 'return';
    undo: boolean;
  } | null>(null);
  // 우클릭한 이벤트 칩의 쪽과 그 쪽의 마침 여부.
  const menuEventSide =
    menu?.ev.calendarEvent && menu.ev.move?.kind === 'event' ? menu.ev.move.side : null;
  const menuEventDone =
    menuEventSide && menu?.ev.calendarEvent
      ? menuEventSide === 'install'
        ? menu.ev.calendarEvent.installed
        : menu.ev.calendarEvent.returned
      : false;
  const closeMenu = useCallback(() => setMenu(null), []);
  // '+N 건 더' 를 누른 날 — 그날 칸을 펼쳐 일정을 모두 보인다(2026-10-01 오너 보고: 무슨
  // 일정인지 확인할 수가 없다). 칸이 늘어나면 그 주(행)만 같이 키가 커진다.
  const [expandedDays, setExpandedDays] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (date: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  const moveMutation = useMutation({
    mutationFn: async ({ ev, date }: { ev: CalendarEvent; date: string }) => {
      if (!ev.move) return;
      if (ev.move.kind === 'infra')
        await scheduleDatesApi.moveInfra(ev.move.id, ev.move.field, date);
      else if (ev.move.kind === 'return') await scheduleDatesApi.moveReturn(ev.move.id, date);
      // 로봇 교체 — 묶인 오더가 함께, 다 마친 교체면 실제 교체일도 같이(2026-10-20 오너).
      else if (ev.move.kind === 'replace')
        await scheduleDatesApi.updateReplacement(ev.move.id, { date });
      // 재계약 — 일정 날짜만(마친 재계약도 실제 회수일 · 배치일은 그대로).
      else if (ev.move.kind === 'renewal')
        await scheduleDatesApi.updateRenewal(ev.move.id, { date });
      // 이벤트 — 그 칩의 날짜(설치일 또는 회수일)만. 회수일이 설치일보다 앞서면 서버가 막는다.
      else if (ev.move.kind === 'event')
        await scheduleDatesApi.updateEvent(
          ev.move.id,
          ev.move.side === 'install' ? { installDate: date } : { returnDate: date },
        );
      // 유지보수 — 서비스 오더의 방문 예정일.
      else await scheduleDatesApi.moveMaintenance(ev.move.id, date);
    },
    onSuccess: async (_d, { ev, date }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['infrastructure-orders'] }),
      ]);
      toast?.success(
        `${CATEGORY_META[ev.category].label} 일정을 옮겼습니다. 새 날짜는 ${date}입니다.`,
      );
    },
    onError: (err) => toast?.error(extractApiError(err), '일정을 옮기지 못했습니다'),
    onSettled: (_d, _e, { ev }) =>
      setPendingMoves((m) => {
        const next = { ...m };
        delete next[ev.id];
        return next;
      }),
  });
  function dropOn(date: string, raw: string) {
    setDropDate(null);
    let id: string;
    try {
      id = (JSON.parse(raw) as { id: string }).id;
    } catch {
      return;
    }
    const ev = allEvents.find((e) => e.id === id);
    if (!ev?.move) return;
    const current = pendingMoves[ev.id] ?? ev.date;
    if (current === date) return;
    setPendingMoves((m) => ({ ...m, [ev.id]: date }));
    moveMutation.mutate({ ev, date });
  }

  const events = useMemo(
    () =>
      allEvents
        .map((e) => (pendingMoves[e.id] ? { ...e, date: pendingMoves[e.id] } : e))
        .filter(
          (e) => activeCategories[e.category] && e.date >= range.start && e.date <= range.end,
        ),
    [allEvents, pendingMoves, activeCategories, range.start, range.end],
  );

  const eventsByDate = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const arr = m.get(e.date);
      if (arr) arr.push(e);
      else m.set(e.date, [e]);
    }
    // 하루 안에서는 하루 종일 일정이 먼저, 그다음 시각 순.
    for (const arr of m.values()) arr.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
    return m;
  }, [events]);

  const weeks = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  function shift(delta: number): void {
    setCursor((c) => {
      const m = c.month + delta;
      if (m < 0) return { year: c.year - 1, month: 11 };
      if (m > 11) return { year: c.year + 1, month: 0 };
      return { year: c.year, month: m };
    });
  }

  function jumpToToday(): void {
    setCursor(kstMonthCursor());
  }

  // '오늘' 테두리가 붙는 칸 — 위 이벤트 키와 **같은 기준(KST)** 이어야 한다.
  // 종전 UTC 읽기는 한국 오전 0~9시에 어제 칸에 테두리를 그렸다.
  const today = kstTodayYmd();

  return (
    <section style={card} aria-label="통합 캘린더">
      <header style={headerRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 className="kuun-h3" style={{ margin: 0 }}>
            통합 캘린더
          </h3>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {ordersQ.isFetching ||
            maintenanceQ.isFetching ||
            returnsQ.isFetching ||
            replacementsQ.isFetching ||
            renewalsQ.isFetching ||
            eventsQ.isFetching
              ? '불러오는 중…'
              : `${events.length}건`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button type="button" onClick={() => shift(-1)} style={navBtn} aria-label="이전 달">
            <Icon name="chev-left" size={16} aria-hidden />
          </button>
          <strong style={{ minWidth: 110, textAlign: 'center', fontSize: 14 }}>
            {cursor.year}년 {String(cursor.month + 1).padStart(2, '0')}월
          </strong>
          <button type="button" onClick={() => shift(1)} style={navBtn} aria-label="다음 달">
            <Icon name="chev-right" size={16} aria-hidden />
          </button>
          {/* v1.0.14 §2 — '오늘' 버튼이 28×28 navBtn 박스 밖으로
              튀어나오는 버그 (사용자 피드백). 한글 2자가 들어가도록
              width 고정 해제 + padding 기반 sizing 으로 전환. */}
          <button type="button" onClick={jumpToToday} style={todayBtn}>
            오늘
          </button>
        </div>
      </header>

      {/* S3 Phase 3.7 (spec §3.8) — 카테고리 표시 토글 바. 범례 칩을
          체크박스로 대체: 표시: [✓ 로봇 설치] [ ] 유지보수 [ ] 그릴러 납품.
          compact 모드에서도 표시 (단순한 컨트롤이라 좁은 컬럼에도 적합). */}
      <div style={toggleBar} role="group" aria-label="캘린더 카테고리 표시 토글">
        <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 600 }}>표시:</span>
        {CATEGORY_ORDER.map((k) => {
          const on = activeCategories[k];
          return (
            <label key={k} style={toggleChip(k, on)}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleCategory(k)}
                style={{ margin: 0, cursor: 'pointer' }}
              />
              <Icon name={CATEGORY_META[k].iconName} size={14} aria-hidden />{' '}
              {CATEGORY_META[k].label}
            </label>
          );
        })}
      </div>

      <div style={gridHeader}>
        {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
          <div
            key={d}
            style={{
              ...gridHeaderCell,
              color: i === 0 ? 'var(--fg-danger)' : i === 6 ? 'var(--blue-800)' : undefined,
            }}
          >
            {d}
          </div>
        ))}
      </div>

      <div style={compact ? gridCompact : grid}>
        {weeks.flat().map((cell) => {
          const isToday = cell.dateStr === today;
          const inMonth = cell.month === cursor.month;
          const dayEvents = eventsByDate.get(cell.dateStr) ?? [];
          const holiday = isKoreanHoliday(cell.dateStr);
          const weekend = isWeekend(cell.dateStr);
          const maxChips = compact ? 2 : 3;
          const expanded = expandedDays.has(cell.dateStr);
          const shown = expanded ? dayEvents : dayEvents.slice(0, maxChips);

          return (
            <div
              key={cell.dateStr}
              data-testid={`cal-day-${cell.dateStr}`}
              style={{
                ...dayCell(inMonth, isToday, compact),
                cursor: canMove ? 'pointer' : undefined,
                ...(dropDate === cell.dateStr
                  ? {
                      outline: '2px dashed var(--fg-brand)',
                      outlineOffset: -2,
                      background: 'color-mix(in srgb, var(--fg-brand) 8%, var(--bg-surface))',
                    }
                  : {}),
              }}
              onDragOver={(e) => {
                if (!canMove || !Array.from(e.dataTransfer.types).includes(DRAG_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropDate !== cell.dateStr) setDropDate(cell.dateStr);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDropDate((d) => (d === cell.dateStr ? null : d));
                }
              }}
              onDrop={(e) => {
                const raw = e.dataTransfer.getData(DRAG_MIME);
                if (!raw) return;
                e.preventDefault();
                dropOn(cell.dateStr, raw);
              }}
              // 칸의 빈 곳을 누르면 그날로 일정 추가 — 일정 칩(링크)·'+N 건 더'는 제 할 일을 한다.
              onClick={(e) => {
                if (!canMove) return;
                if ((e.target as HTMLElement).closest('a, button, [draggable="true"]')) return;
                setNewScheduleDate(cell.dateStr);
              }}
              title={canMove ? '눌러서 이 날에 일정 추가' : undefined}
            >
              <div style={dayHeader}>
                <span style={dateWithTag}>
                  <span
                    role={canMove ? 'button' : undefined}
                    tabIndex={canMove ? 0 : undefined}
                    aria-label={canMove ? `${cell.dateStr} 일정 추가` : undefined}
                    data-testid={`cal-add-${cell.dateStr}`}
                    onKeyDown={(e) => {
                      if (canMove && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setNewScheduleDate(cell.dateStr);
                      }
                    }}
                    style={{
                      cursor: canMove ? 'pointer' : undefined,
                      fontSize: 12,
                      fontWeight: isToday ? 700 : 500,
                      color: holiday
                        ? 'var(--fg-danger)'
                        : weekend && cell.day === 0
                          ? 'var(--fg-danger)'
                          : weekend && cell.day === 6
                            ? 'var(--blue-800)'
                            : inMonth
                              ? 'var(--fg-default)'
                              : 'var(--fg-muted)',
                    }}
                  >
                    {cell.dateStr.slice(8, 10)}
                  </span>
                  {isToday && (
                    <span style={todayTag} data-testid={`cal-today-${cell.dateStr}`}>
                      오늘
                    </span>
                  )}
                </span>
                {holiday && (
                  <span
                    title={getHolidayName(cell.dateStr)}
                    style={{ fontSize: 9, color: 'var(--fg-danger)' }}
                  >
                    {getHolidayName(cell.dateStr)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shown.map((ev) => {
                  const meta = CATEGORY_META[ev.category];
                  const draggable = canMove && !!ev.move;
                  const saving = !!pendingMoves[ev.id];
                  // 아직 안 마친 로봇 교체 — 누르면 [교체 완료] 창(우클릭 메뉴와 같은 창).
                  const completable =
                    canMove &&
                    ((!!ev.replacement && !ev.replacement.done) ||
                      (!!ev.renewal && !ev.renewal.done));
                  // 이벤트 칩 — 누르면 이벤트 창(보기 · 고치기).
                  const openable = canMove && !!ev.calendarEvent;
                  const open = () => {
                    if (ev.calendarEvent) setOpenEvent(ev.calendarEvent);
                    else if (completable) setCompleting(ev);
                  };
                  const solid = meta.solid;
                  const inner = (
                    <span
                      data-testid={`cal-event-${ev.id}`}
                      draggable={draggable}
                      {...(ev.replacement || ev.renewal || ev.calendarEvent
                        ? {
                            // 교체 칩은 링크가 아니라 칸의 '일정 추가'로 새지 않게 막는다.
                            onClick: (e: MouseEvent) => {
                              e.stopPropagation();
                              if (completable || openable) open();
                            },
                            ...(completable || openable
                              ? {
                                  role: 'button',
                                  tabIndex: 0,
                                  onKeyDown: (e: KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      open();
                                    }
                                  },
                                }
                              : {}),
                          }
                        : {})}
                      onDragStart={(e) => {
                        if (!draggable) return;
                        e.stopPropagation();
                        e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ id: ev.id }));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => setDropDate(null)}
                      onContextMenu={(e) => {
                        if (!canMove || !ev.move) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setMenu({ ev, x: e.clientX, y: e.clientY });
                      }}
                      style={{
                        ...eventChip,
                        background: solid ? solid[0] : meta.bg,
                        color: solid ? solid[1] : meta.color,
                        ...(solid ? { fontWeight: 600 } : {}),
                        cursor: draggable ? 'grab' : eventChip.cursor,
                        opacity: saving ? 0.6 : eventSideDone(ev) ? 0.55 : 1,
                      }}
                      title={`${meta.label}: ${ev.time ? `${ev.time} ` : ''}${ev.title}${ev.subtitle ? ` (${ev.subtitle})` : ''}${
                        openable
                          ? `. 누르면 이벤트를 보고 고칠 수 있습니다.${eventSideDone(ev) ? '' : ` 우클릭에서 ${ev.move?.kind === 'event' && ev.move.side === 'install' ? '설치' : '회수'} 완료를 누릅니다.`}`
                          : completable
                            ? ev.renewal
                              ? '. 누르면 재계약을 마칠 수 있습니다.'
                              : '. 누르면 교체를 마칠 수 있습니다.'
                            : ''
                      }${draggable ? `${completable || openable ? '' : '.'} 끌어서 다른 날로 옮길 수 있고, 우클릭하면 고치거나 지울 수 있습니다.` : ''}`}
                    >
                      <Icon name={CATEGORY_META[ev.category].iconName} size={11} aria-hidden />{' '}
                      {ev.title}
                    </span>
                  );
                  return ev.href ? (
                    <Link
                      key={ev.id}
                      to={ev.href}
                      style={{ textDecoration: 'none' }}
                      // 링크 자체가 끌리면(브라우저 기본) 주소가 끌려 일정 옮기기가 안 된다.
                      draggable={false}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <span key={ev.id}>{inner}</span>
                  );
                })}
                {dayEvents.length > maxChips && (
                  <button
                    type="button"
                    data-testid={`cal-more-${cell.dateStr}`}
                    aria-expanded={expanded}
                    onClick={() => toggleExpanded(cell.dateStr)}
                    style={moreButton}
                    title={expanded ? '접기' : `숨은 일정 ${dayEvents.length - maxChips}건 보기`}
                  >
                    {expanded ? '접기' : `+${dayEvents.length - maxChips} 건 더`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {newScheduleDate && (
        <NewScheduleModal
          date={newScheduleDate}
          onClose={() => setNewScheduleDate(null)}
          // 꺼 둔 분류로 추가했으면 켜서 바로 보이게.
          onAdded={(cat) => setActiveCategories((prev) => ({ ...prev, [cat]: true }))}
        />
      )}
      {menu && (
        <ScheduleContextMenu
          x={menu.x}
          y={menu.y}
          onClose={closeMenu}
          onEdit={() => {
            const ev = menu.ev;
            setMenu(null);
            if (ev.calendarEvent) setOpenEvent(ev.calendarEvent);
            else if (ev.move) setEditing({ ...ev, move: ev.move });
          }}
          onDelete={() => {
            const ev = menu.ev;
            setMenu(null);
            if (ev.move) setDeleting({ ...ev, move: ev.move });
          }}
          onComplete={
            (menu.ev.replacement && !menu.ev.replacement.done) ||
            (menu.ev.renewal && !menu.ev.renewal.done)
              ? () => {
                  const ev = menu.ev;
                  setMenu(null);
                  setCompleting(ev);
                }
              : // 2026-11-02 — 이벤트: 설치 칩 [설치 완료](출고 기록), 회수 칩 [회수 완료](반납 · 정비).
                // 회수를 마친 이벤트의 설치 칩에는 없다(회수부터 되돌린다).
                menuEventSide && !menuEventDone && !menu.ev.calendarEvent!.returned
                ? () => {
                    const e = menu.ev.calendarEvent!;
                    setMenu(null);
                    setEventStage({ event: e, stage: menuEventSide, undo: false });
                  }
                : undefined
          }
          completeLabel={
            menu.ev.renewal
              ? '재계약 완료'
              : menuEventSide
                ? menuEventSide === 'install'
                  ? '설치 완료'
                  : '회수 완료'
                : '교체 완료'
          }
          onUndo={
            menu.ev.renewal?.done
              ? () => {
                  const r = menu.ev.renewal!;
                  setMenu(null);
                  setUndoingRenewal(r);
                }
              : menuEventSide &&
                  menuEventDone &&
                  // 설치 완료 되돌리기는 회수 전에만(회수를 마쳤으면 회수부터).
                  (menuEventSide === 'return' || !menu.ev.calendarEvent!.returned)
                ? () => {
                    const e = menu.ev.calendarEvent!;
                    setMenu(null);
                    setEventStage({ event: e, stage: menuEventSide, undo: true });
                  }
                : undefined
          }
        />
      )}
      {completing?.renewal && (
        <CompleteRenewalModal
          renewal={completing.renewal}
          date={completing.date}
          onClose={() => setCompleting(null)}
        />
      )}
      {undoingRenewal && (
        <UndoRenewalDialog renewal={undoingRenewal} onClose={() => setUndoingRenewal(null)} />
      )}
      {completing?.replacement && (
        <CompleteReplacementModal
          replacement={completing.replacement}
          date={completing.date}
          onClose={() => setCompleting(null)}
        />
      )}
      {openEvent && <EventEditModal event={openEvent} onClose={() => setOpenEvent(null)} />}
      {eventStage && (
        <EventStageDialog
          event={eventStage.event}
          stage={eventStage.stage}
          undo={eventStage.undo}
          onClose={() => setEventStage(null)}
        />
      )}
      {editing && <EditScheduleModal target={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteScheduleDialog target={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}

// ============================================================
// 격자 만들기 — 셀 라벨은 **정본 한 벌**(kstYmdHyphen)로 찍는다
// ============================================================
//
// 한국시간 통일 5차(26-08-14) — 여기 있던 지역 `formatYmd`(UTC 성분 읽기)를 지웠다.
// 격자 날짜는 `Date.UTC(y, m, d)` 로 만든 **UTC 자정**이라 KST 로 읽어도 같은
// 날짜다(자정 + 9h = 그날 09:00). 출력은 종전과 동일하면서 이 파일에서 시간대
// 규칙을 아는 코드가 사라진다 — '오늘'만 고치고 격자를 안 고쳐 둘이 갈리는
// 사고를 구조적으로 막는다.

/** 격자용 UTC 자정 Date → "YYYY-MM-DD". 항상 유효하므로 폴백은 방어용. */
function gridYmd(d: Date): string {
  return kstYmdHyphen(d) ?? '';
}

interface GridCell {
  dateStr: string;
  month: number;
  day: number; // day-of-week (0..6)
}

function buildMonthGrid(year: number, month: number): GridCell[][] {
  // Start from the Sunday on/before the 1st; end on the Saturday on/after
  // the last day. Always 6 weeks × 7 days = 42 cells (some months show
  // 5 visible weeks but the data shape is uniform).
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());

  const weeks: GridCell[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: GridCell[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        dateStr: gridYmd(cursor),
        month: cursor.getUTCMonth(),
        day: cursor.getUTCDay(),
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function monthGridRange(year: number, month: number): { start: string; end: string } {
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 41);
  return { start: gridYmd(start), end: gridYmd(end) };
}

// ============================================================
// Styles
// ============================================================

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--bg-surface)',
  padding: 16,
};

const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  flexWrap: 'wrap',
  gap: 8,
};

const navBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  width: 28,
  height: 28,
  cursor: 'pointer',
  fontSize: 14,
  color: 'var(--fg-default)',
};

// v1.0.14 §2 — '오늘' (Korean 2-char) 버튼 전용 스타일. navBtn 의
// 고정 28×28 박스를 쓰면 글자가 박스 밖으로 튀어나옴. padding 기반
// auto-width + 한 단계 작은 폰트로 헤더 라인 안에 깔끔히 정렬.
const todayBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  height: 28,
  padding: '0 10px',
  marginLeft: 8,
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: 'var(--fg-default)',
};

// S3 Phase 3.7 (spec §3.8) — 카테고리 표시 토글 바. (기존 legendRow 대체.)
const toggleBar: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  marginBottom: 8,
};

function toggleChip(c: CalendarCategory, on: boolean): CSSProperties {
  const m = CATEGORY_META[c];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid',
    borderColor: on ? 'transparent' : 'var(--border-default)',
    background: on ? m.bg : 'transparent',
    color: on ? m.color : 'var(--fg-muted)',
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
  };
}

const gridHeader: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderBottom: '1px solid var(--border-subtle)',
};

const gridHeaderCell: CSSProperties = {
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 600,
  padding: '6px 0',
  color: 'var(--fg-muted)',
};

// windows-ux-edit (2026-06, 슬라이드2 + 사용자 후속 피드백) — 통합 캘린더 세로 크기.
//   • 비-compact(grid): 인프라 발주 페이지가 사용. 슬라이드2 확장(120)이
//     이 페이지에 적용돼 과도하게 커졌다는 피드백 → 원래 80 으로 원상 복구.
//   • compact(gridCompact): 통합 대시보드가 사용(3:2 split). 대시보드 캘린더만
//     세로를 조금 확장(80→100). dayCell minHeight 도 동일 값으로 맞춘다.
const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: 'minmax(80px, auto)',
};

const moreButton: CSSProperties = {
  alignSelf: 'flex-start',
  border: 'none',
  background: 'none',
  padding: '0 2px',
  fontSize: 10,
  color: 'var(--fg-brand)',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const gridCompact: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: 'minmax(100px, auto)',
};

function dayCell(inMonth: boolean, isToday: boolean, compact = false): CSSProperties {
  return {
    border: '1px solid var(--border-default)',
    borderTop: 'none',
    borderLeft: 'none',
    padding: compact ? 3 : 4,
    background: isToday ? 'var(--blue-50)' : inMonth ? 'var(--bg-surface)' : 'var(--bg-sunken)',
    display: 'flex',
    flexDirection: 'column',
    gap: compact ? 2 : 4,
    minHeight: compact ? 100 : 80,
    overflow: 'hidden',
  };
}

/**
 * 오늘 칸의 '오늘' 꼬리표 — 날짜 글자와 같은 크기, 진한 브랜드색 바탕에 흰 글씨(2026-10-23 오너 "날짜 옆에 날짜 텍스트
 * 크기만한 크기로 '오늘' … 잘보이는 색으로 강조"). 칸 바탕(--blue-50)보다 훨씬 진해 한눈에 띈다.
 */
const todayTag: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '16px',
  padding: '0 6px',
  borderRadius: 999,
  background: 'var(--brand)',
  color: 'var(--brand-on)',
  whiteSpace: 'nowrap',
};
const dateWithTag: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4 };

const dayHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
};

/** 이벤트 칩의 그 쪽 일을 마쳤나 — 설치 칩은 설치 완료, 회수 칩은 회수 완료(마친 칩은 흐리게). */
function eventSideDone(ev: CalendarEvent): boolean {
  const e = ev.calendarEvent;
  if (!e || ev.move?.kind !== 'event') return false;
  // 출고 기록과 잇기 전에 회수를 마친 이벤트는 설치 완료 표시가 없다 — 회수를 마쳤으면 설치도 마친 것.
  return ev.move.side === 'install' ? e.installed || e.returned : e.returned;
}

const eventChip: CSSProperties = {
  display: 'inline-block',
  width: '100%',
  fontSize: 10,
  padding: '2px 6px',
  borderRadius: 'var(--radius-sm)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
